import type { ReactElement } from 'react';
import { assetToast } from '@xdocs/i18n';
import { Button, Modal, cn, useI18n, useToast } from '@xdocs/ui';
import { useEditorT } from './useEditorT.js';
import styles from './RestoreDraftPrompt.module.css';

export interface RestoreDraftPromptProps {
  open: boolean;
  /** Required — names the asset (§16.1), never "this document". */
  documentTitle: string;
  onRestore: () => void;
  onDiscard: () => void;
}

/**
 * E20 §5.10-3: "editor content ... conflict on reopen → 'restore draft?'
 * prompt naming the document, never silent clobber." Two explicit actions
 * (not a single confirm/cancel) so `ConfirmDialog`'s binary shape doesn't
 * fit — built directly on `Modal`.
 */
export function RestoreDraftPrompt({ open, documentTitle, onRestore, onDiscard }: RestoreDraftPromptProps): ReactElement | null {
  const { t } = useEditorT();
  const { locale } = useI18n();
  const { push: pushToast } = useToast();

  if (!open) return null;

  function handleRestore(): void {
    onRestore();
    pushToast(assetToast(locale, 'info', t('editor:toast.draftRestored.action'), documentTitle));
  }

  function handleDiscard(): void {
    onDiscard();
    pushToast(assetToast(locale, 'info', t('editor:toast.draftDiscarded.action'), documentTitle));
  }

  return (
    <Modal open={open} onClose={handleDiscard} title={t('editor:restoreDraft.title')} size="sm">
      <p>{t('editor:restoreDraft.description', { title: documentTitle })}</p>
      <div className={cn(styles['actions'])}>
        <Button variant="secondary" onClick={handleDiscard}>
          {t('editor:restoreDraft.discard')}
        </Button>
        <Button variant="primary" onClick={handleRestore}>
          {t('editor:restoreDraft.restore')}
        </Button>
      </div>
    </Modal>
  );
}
